import React, { useState, useEffect } from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import AxiosController from '../../../../authentication/AxiosController';
import Loader from '../../../../loader/Loader';

const Marketers = () => {
  const { slug, lang } = useParams();
  const { t } = useTranslation("global");
  const token = localStorage.getItem("token");
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [marketer, setMarketer] = useState([]);
  const [userId, setUserId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [referredUserIds, setReferredUserIds] = useState([]);

  const [sortConfig, setSortConfig] = useState({
    key: 'approved_payment_requests',
    direction: 'descending',
  });

  const box_head_dir = lang === "en" ? "ltr" : "rtl";

  const formatAmount = (amount) => {
    return parseFloat(amount || 0).toLocaleString();
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? ' 🔼' : ' 🔽';
    }
    return ' ↕️';
  };

  const sortedData = React.useMemo(() => {
    let sortableItems = [...marketer];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'user_ids') {
          aValue = Array.isArray(aValue) ? aValue.length : 0;
          bValue = Array.isArray(bValue) ? bValue.length : 0;
        } else if (!isNaN(aValue) && !isNaN(bValue)) {
          aValue = Number(aValue);
          bValue = Number(bValue);
        }

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [marketer, sortConfig]);

  const get_marketer = () => {
    setLoading(true);
    AxiosController.get(`/api/${slug}/${lang}/reports/payments`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        role: "affiliate",
        start_date: startDate,
        end_date: endDate,
        user_ids: referredUserIds,
        user_id: userId,
      },
    })
      .then((res) => {
        setMarketer(res?.data || []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    setUserId(urlParams.get("user_id") || "");
    setStartDate(urlParams.get("start_date") || "");
    setEndDate(urlParams.get("end_date") || "");
    const referredIds = urlParams.get("user_ids");
    setReferredUserIds(referredIds ? referredIds.split(',') : []);
  }, [location.search]);

  useEffect(() => {
    get_marketer();
  }, [userId, startDate, endDate, referredUserIds]);

  const handleReferredUserIdsFilter = (ids) => {
    if (!ids || !Array.isArray(ids)) return;
    const newUrlParams = new URLSearchParams(location.search);
    newUrlParams.set('user_ids', ids.join(','));
    window.history.pushState({}, '', `${location.pathname}?${newUrlParams.toString()}`);
    setReferredUserIds(ids);
  };

  const clearReferredUserIdsFilter = () => {
    const newUrlParams = new URLSearchParams(location.search);
    newUrlParams.delete('user_ids');
    window.history.pushState({}, '', `${location.pathname}?${newUrlParams.toString()}`);
    setReferredUserIds([]);
  };
 

  return (
    <div className="row" dir={box_head_dir} style={{ height: '1200px' }}>
      <div className="col-12" style={{ position: 'relative', overflowX: 'auto', height: '100%' }}>
      <Loader active={loading} />

        {/* Clear Filter Button */}
        {referredUserIds.length > 0 && (
          <div className="mb-3 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-danger"
              onClick={clearReferredUserIdsFilter}
            >
              {t("panel.clearUserIdsFilter") || "Clear Filter"}
            </button>
          </div>
        )}

        <Table responsive dir={box_head_dir} className="reports-table text-nowrap text-center">
          <thead>
            <tr className="text-center">
              <th onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}>
                {t("home.fullname")}
                {getSortIndicator('id')}
              </th>
              <th onClick={() => handleSort('balance')} style={{ cursor: 'pointer' }}>
                {t("panel.balance")}
                {getSortIndicator('balance')}
              </th>
              <th onClick={() => handleSort('confirmed_money')} style={{ cursor: 'pointer' }}>
                {t("panel.confirmedMoney")}
                {getSortIndicator('confirmed_money')}
              </th>
              <th onClick={() => handleSort('expected_money')} style={{ cursor: 'pointer' }}>
                {t("panel.expectedMoney")}
                {getSortIndicator('expected_money')}
              </th>
              <th onClick={() => handleSort('approved_payment_requests')} style={{ cursor: 'pointer' }}>
                {t("panel.approvedPaymentRequests")}
                {getSortIndicator('approved_payment_requests')}
              </th>
              <th onClick={() => handleSort('sys_commission')} style={{ cursor: 'pointer' }}>
                {t("panel.sys_commission")}
                {getSortIndicator('sys_commission')}
              </th>
              <th onClick={() => handleSort('referral_amount')} style={{ cursor: 'pointer' }}>
                {t("panel.referral_link_value")}
                {getSortIndicator('referral_amount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => (
              <tr key={index} className="text-center">
                <td>
                  <span className="fw-bold">#{item.id}</span><br />
                  {item.first_name} {item.last_name}
                </td>
                <td>{formatAmount(item.balance)}</td>
                <td>{formatAmount(item.confirmed_money)}</td>
                <td>{formatAmount(item.expected_money)}</td>
                <td>{formatAmount(item.approved_payment_requests)}</td>
                <td>{formatAmount(item.sys_commission)}</td>
                <td
                  onClick={() => handleReferredUserIdsFilter(item.referred_user_ids)}
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {formatAmount(item.referral_amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default Marketers;
